import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "oidc-client-ts";

import type { RuntimeConfig } from "../config/runtime";
import { replaceLocation } from "../lib/browserNavigation";
import { AuthContext, type AuthContextValue, type AuthStatus } from "./AuthContext";
import { buildCognitoLogoutUrl, getOidcUserManager, isAuthenticatedUser } from "./oidc";
import { clearReturnPath, consumeReturnPath, sanitizeAppRedirectPath, storeReturnPath } from "./redirects";

export function AuthProvider({ config, children }: { config: RuntimeConfig | null; children: ReactNode }) {
  const auth = config?.auth ?? null;
  const isCallbackRoute = window.location.pathname.replace(/\/+$/, "") === "/auth/callback";
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(() => {
    let active = true;

    if (!auth) {
      setUser(null);
      setError(null);
      setStatus("unconfigured");
      return undefined;
    }

    if (isCallbackRoute) {
      setStatus("loading");
      return undefined;
    }

    const manager = getOidcUserManager(auth);

    const syncUser = (nextUser: User | null) => {
      if (!active) {
        return;
      }

      setUser(nextUser);
      setError(null);
      setStatus(isAuthenticatedUser(nextUser) ? "authenticated" : "anonymous");
    };

    const handleSilentRenewError = () => {
      if (active) {
        setUser(null);
        setStatus("anonymous");
      }
    };

    const removeLoaded = manager.events.addUserLoaded((nextUser) => syncUser(nextUser));
    const removeUnloaded = manager.events.addUserUnloaded(() => syncUser(null));
    const removeExpired = manager.events.addAccessTokenExpired(() => syncUser(null));
    const removeSilentError = manager.events.addSilentRenewError(handleSilentRenewError);

    setStatus("loading");
    manager
      .getUser()
      .then(async (storedUser) => {
        if (!active) {
          return;
        }

        if (isAuthenticatedUser(storedUser)) {
          syncUser(storedUser);
          return;
        }

        if (storedUser?.expired) {
          try {
            const renewedUser = await manager.signinSilent();
            syncUser(renewedUser);
            return;
          } catch {
            await manager.removeUser();
          }
        }

        syncUser(null);
      })
      .catch(() => {
        if (active) {
          setUser(null);
          setError("We could not restore your MedoPilot session.");
          setStatus("error");
        }
      });

    return () => {
      active = false;
      removeLoaded();
      removeUnloaded();
      removeExpired();
      removeSilentError();
    };
  }, [auth, isCallbackRoute]);

  const signIn = useCallback(
    async (returnTo?: string) => {
      if (!auth) {
        setStatus("unconfigured");
        return;
      }

      if (isSigningOut) {
        return;
      }

      const safeReturnTo = sanitizeAppRedirectPath(returnTo) ?? "/app";
      storeReturnPath(safeReturnTo);
      setError(null);
      await getOidcUserManager(auth).signinRedirect({
        state: { returnTo: safeReturnTo },
      });
    },
    [auth, isSigningOut],
  );

  const completeSignIn = useCallback(async () => {
    if (!auth) {
      throw new Error("MedoPilot access is not configured.");
    }

    const nextUser = await getOidcUserManager(auth).signinRedirectCallback(window.location.href);
    setUser(nextUser);
    setError(null);
    setStatus("authenticated");

    const stateReturnTo =
      nextUser.state && typeof nextUser.state === "object" && "returnTo" in nextUser.state
        ? sanitizeAppRedirectPath((nextUser.state as { returnTo?: unknown }).returnTo)
        : null;
    const storedReturnTo = consumeReturnPath("/app");

    return stateReturnTo ?? storedReturnTo;
  }, [auth]);

  const signOut = useCallback(async () => {
    if (!auth) {
      setUser(null);
      setStatus("unconfigured");
      return;
    }

    const manager = getOidcUserManager(auth);
    const logoutUrl = buildCognitoLogoutUrl(auth);

    setIsSigningOut(true);
    clearReturnPath();
    setError(null);
    try {
      await manager.removeUser();
    } finally {
      setUser(null);
      setStatus("anonymous");
      replaceLocation(logoutUrl);
    }
  }, [auth]);

  const clearError = useCallback(() => {
    setError(null);
    setStatus(auth ? "anonymous" : "unconfigured");
  }, [auth]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      error,
      isConfigured: Boolean(auth),
      isAuthenticated: status === "authenticated" && isAuthenticatedUser(user),
      isSigningOut,
      signIn,
      completeSignIn,
      signOut,
      clearError,
    }),
    [auth, clearError, completeSignIn, error, isSigningOut, signIn, signOut, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
